import * as React from 'react'
import { cn } from '../../../lib/cn'

/** Hard-coded primary method list. Order is significant — matches the backend enum. */
export const PRIMARY_METHODS = [
  { name: 'password', label: 'Password' },
  { name: 'passwordless-email', label: 'Passwordless (email)' },
  { name: 'passwordless-sms', label: 'Passwordless (SMS)' },
  { name: 'magic-link', label: 'Magic link' },
  { name: 'webauthn-passkey', label: 'WebAuthn / Passkey' },
] as const

export type PrimaryMethodName = (typeof PRIMARY_METHODS)[number]['name']

export interface PrimaryMethodPickerProps {
  /** Selected method names. Subset of PRIMARY_METHODS[].name. */
  value: string[]
  onChange: (next: string[]) => void
  /**
   * Highlight a specific method chip when the publish-validator returns
   * FLOW_REF_UNKNOWN_PRIMARY. Pass the offending method name.
   */
  errorFor?: string
  disabled?: boolean
  className?: string
}

/**
 * PrimaryMethodPicker — multi-select chip row for primary authentication methods.
 *
 * Options are statically defined — the set of primary methods is a platform
 * constant and does not vary per tenant. If the backend publish-validator
 * returns FLOW_REF_UNKNOWN_PRIMARY the `errorFor` prop highlights the
 * offending chip.
 *
 * Accessibility contract:
 * - Each chip is a `<button>` with `aria-pressed` indicating selection.
 * - The error chip receives `aria-invalid` and an `aria-describedby`.
 */
export const PrimaryMethodPicker = React.forwardRef<HTMLDivElement, PrimaryMethodPickerProps>(
  ({ value, onChange, errorFor, disabled = false, className }, ref) => {
    const toggle = (name: string) => {
      if (disabled) return
      const next = value.includes(name) ? value.filter((v) => v !== name) : [...value, name]
      onChange(next)
    }

    return (
      <div ref={ref} className={cn('flex flex-wrap gap-1.5', className)}>
        {PRIMARY_METHODS.map((method) => {
          const isSelected = value.includes(method.name)
          const hasError = method.name === errorFor
          const errorId = hasError ? `pmp-error-${method.name}` : undefined

          return (
            <React.Fragment key={method.name}>
              <button
                type="button"
                aria-pressed={isSelected}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={errorId}
                disabled={disabled}
                onClick={() => toggle(method.name)}
                className={cn(
                  'rounded-pill border px-2.5 py-1 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                  'focus-visible:ring-ia-border-focus',
                  disabled && 'cursor-not-allowed opacity-50',
                  hasError
                    ? 'border-ia-error bg-ia-error/10 text-ia-error'
                    : isSelected
                      ? 'border-brand-500/40 bg-brand-500/15 text-foreground hover:bg-brand-500/25'
                      : 'border-ia-border bg-transparent text-muted-foreground hover:border-ia-border-strong hover:text-foreground',
                )}
              >
                {method.label}
              </button>
              {hasError && (
                <span id={errorId} className="sr-only">
                  Unknown primary method &ldquo;{method.name}&rdquo; — not a recognised platform
                  method.
                </span>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  },
)

PrimaryMethodPicker.displayName = 'PrimaryMethodPicker'
